import {
    getSessionStorageItemJson,
    setSessionStorageItemJson,
} from '@vfde-sails/storage';
import {
    debugInfo,
    getExpirationDate,
    getQueryParam,
} from '@vfde-sails/utils';
import { SessionAppState } from '../interface';
import { AppState } from '../container/App/interface';
import { initialState } from '../container/App/reducer';
import { State } from '../container/App/selector';
import {
    SESSION_KEY,
    STORAGE_KEY,
} from '../constant';
import { getCMSConfig } from './cms';
import { QueryNames } from '../enum';
import { isValidId } from './typeGuard';

/**
 * Updates the state from the CMS options on pageload and
 * sets `selectedTariffId`.
 *
 * @param draftState - State to be modified
 * @returns Modified state
 */
const getStateFromPageOptions = (draftState: AppState) => {
    draftState.selectedTariffId = getCMSConfig().selectedTariffId;

    return draftState;
};

/**
 * Updates the state from the saved session on pageload
 * Does not update the state if the session state is expired
 *
 * @param draftState - State to be modified
 * @returns Modified state
 */
const getStateFromSession = (draftState: AppState) => {
    const sessionState: SessionAppState | null = getSessionStorageItemJson(STORAGE_KEY);
    const appState: Partial<AppState> | undefined = sessionState?.[SESSION_KEY];

    if (!sessionState || !appState) {
        debugInfo('Failed to load state from session storage.');

        return draftState;
    }

    if (new Date(sessionState[SESSION_KEY].expiry) < new Date()) {
        debugInfo('Session expired!');

        return draftState;
    }

    return { ...draftState, ...appState };
};

/**
 * Updates the state from the query parameters on pageload.
 *
 * @param draftState - State to be modified
 * @returns Modified state
 */
const getStateFromQuery = (draftState: AppState) => {
    const tariffId = getQueryParam(QueryNames.Product);
    const cmsTariffIds = Object.values(getCMSConfig().displayedTariffIds);

    if (isValidId(tariffId, cmsTariffIds)) {
        draftState.selectedTariffId = tariffId;
    }

    return draftState;
};

/**
 * Loads state from sessionStorage, additionalPageOptions, query params & cookies.
 *
 * @returns AppState
 */
export const loadInitialAppState = (): Partial<AppState> => {
    try {
        // Note: We need a deep copy of the initialState, becasue 'immer' makes it read-only.
        let draftState: AppState = JSON.parse(JSON.stringify(initialState));

        draftState = getStateFromPageOptions(draftState);
        draftState = getStateFromSession(draftState);
        draftState = getStateFromQuery(draftState);

        return draftState;
    }
    catch {
        debugInfo('Error while parsing app state.');

        return initialState;
    }
};

/**
 * Saves the AppState to the session, while adding an expiry date of 1 day.
 *
 * @param state - State to be saved
 * @param state.tariffs - Tariffs in state
 */
export const saveStateToSession = ({ tariffs }: State) => {
    const sessionAppState: SessionAppState = {
        [SESSION_KEY]: {
            tariffs,
            expiry: getExpirationDate(1).toISOString(),
        },
    };

    setSessionStorageItemJson(STORAGE_KEY, sessionAppState);
};
